import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect
} from 'react';
import {
  RiCheckLine,
  RiCloseLine,
  RiEyeLine,
  RiEyeOffLine
} from 'react-icons/ri';
import './Input.css';

function Input({
  errorMessage,
  helperMessage,
  id,
  isInvalid,
  isRequired,
  isValid,
  label,
  checkLabelHeight,
  name,
  type,
  onBlur,
  onChange,
  isTextarea,
  value,
  ...rest
}) {
  const [isVisible, toggleVisibility] = useState(false);
  const labelRef = useRef(null);
  const [inputTopPadding, setInputTopPadding] = useState();
  useEffect(() => {
    if (checkLabelHeight) setInputTopPadding(labelRef.current.clientHeight + 5);
  });

  const isPassword = type === 'password';

  const inputClassNames = useMemo(
    () =>
      clsx(
        'peer relative box-border appearance-none pl-4 pb-2.5 pt-5 w-full border-solid placeholder-transparent text-base text-black bg-white border-0 ring-1 ring-gray focus:outline-none focus:ring-black md:text-sm rounded-none',
        {
          'ring-green': !isPassword && isValid
        },
        {
          'ring-cinnebar': !isPassword && isInvalid
        }
      ),
    [isInvalid, isPassword, isValid]
  );
  const textareaClassNames = useMemo(
    () =>
      clsx(
        'peer relative box-border appearance-none pl-4 pb-2.5 pt-5 w-full border-solid placeholder-transparent text-base text-black bg-white border-0 ring-1 ring-gray focus:outline-none focus:ring-black md:text-sm rounded-none resize-y',
        {
          'ring-green': !isPassword && isValid
        },
        {
          'ring-cinnebar': !isPassword && isInvalid
        }
      ),
    [isInvalid, isPassword, isValid]
  );
  const labelClassNames = useMemo(
    () =>
      clsx(
        'absolute text-sm text-dark-gray cursor-text duration-150 transform -translate-y-3.5 top-4 origin-[0] left-0 px-4 peer-focus:text-dark-gray peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:-translate-y-3.5 resize-none'
      ),

    []
  );

  const buttonClassNames = useMemo(
    () =>
      clsx(
        'absolute right-0 top-1/2 box-border h-12 w-12 -translate-y-1/2 appearance-none items-center border-0 bg-transparent text-black hover:cursor-pointer'
      ),
    []
  );

  const iconClassNames = useMemo(
    () =>
      clsx(
        'visible absolute top-1/2 right-4 h-6 w-6 -translate-y-1/2 peer-focus:invisible',
        { 'text-green': isValid },
        { 'text-cinnebar': isInvalid },
        { 'peer-placeholder-shown:invisible': !isInvalid }
      ),
    [isValid, isInvalid]
  );

  const messageClassName = useMemo(
    () => clsx('absolute left-0 -bottom-0.5 translate-y-full text-xs'),
    []
  );

  const getStateIcon = useMemo(() => {
    if (isInvalid) {
      return <RiCloseLine className={iconClassNames} />;
    }
    if (isValid) {
      return <RiCheckLine className={iconClassNames} />;
    }
    return null;
  }, [isValid, isInvalid]);

  const getShowHideIcon = useMemo(() => {
    if (isVisible) {
      return <RiEyeOffLine className="h-4 w-4" />;
    }
    return <RiEyeLine className="h-4 w-4" />;
  }, [isVisible]);

  const getHelperText = useMemo(() => {
    if (isInvalid && errorMessage !== '') {
      return (
        <>
          <span
            className={clsx(
              messageClassName,
              'text-cinnebar peer-focus:hidden'
            )}
          >
            {errorMessage}
          </span>
          <span
            className={clsx(
              messageClassName,
              'hidden text-black peer-focus:inline'
            )}
          >
            {helperMessage}
          </span>
        </>
      );
    }
    if (helperMessage !== '') {
      return (
        <span
          className={clsx(
            messageClassName,
            'hidden text-black peer-focus:inline'
          )}
        >
          {helperMessage}
        </span>
      );
    }
    return null;
  }, [errorMessage, helperMessage, isInvalid]);

  const getType = useCallback(() => {
    if (isPassword && !isVisible) {
      return 'password';
    }
    if (isPassword && isVisible) {
      return 'text';
    }
    return type;
  }, [isPassword, isVisible, type]);

  return (
    <div className="relative mb-8">
      {isTextarea ? (
        <textarea
          className={textareaClassNames}
          type={getType()}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={label}
          required={isRequired}
          {...rest}
          rows="6"
          style={
            checkLabelHeight && inputTopPadding !== undefined
              ? { paddingTop: inputTopPadding }
              : {}
          }
        />
      ) : (
        <input
          className={inputClassNames}
          type={getType()}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={label}
          required={isRequired}
          {...rest}
        />
      )}

      <label className={labelClassNames} htmlFor={id} ref={labelRef}>
        {label}
        {isRequired && <span className={clsx('ml-1 text-cinnebar')}>*</span>}
      </label>

      {!isPassword && getStateIcon}

      {isPassword && (
        <button
          className={buttonClassNames}
          aria-label={isVisible ? 'Hide Password' : 'Show Password'}
          type="button"
          onClick={() => toggleVisibility(!isVisible)}
        >
          {getShowHideIcon}
        </button>
      )}
      {getHelperText}
    </div>
  );
}

Input.propTypes = {
  errorMessage: PropTypes.string,
  helperMessage: PropTypes.string,
  id: PropTypes.string.isRequired,
  isInvalid: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isRequired: PropTypes.bool,
  checkLabelHeight: PropTypes.bool,
  isValid: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['date', 'email', 'number', 'password', 'tel', 'text']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

Input.defaultProps = {
  errorMessage: '',
  helperMessage: '',
  isInvalid: undefined,
  isRequired: false,
  checkLabelHeight: false,
  isValid: undefined,
  onBlur: () => {},
  type: 'text',
  value: undefined
};

export default Input;
